import _ from 'lodash';

// A Number's subtext: free text with {placeholders} for the values of the
// block's metrics, e.g. "{totalDrivers} drivers on the road". A Text block's
// can also show each metric's change from its comparison period:
// {totalDrivers.delta} and {totalDrivers.percentDelta}.

// What a placeholder shows of its metric. Absent is its value.
export type PlaceholderMeasure = 'delta' | 'percentDelta';

export type SubtextPart =
  | { kind: 'text'; text: string }
  | { kind: 'metric'; metricId: string; measure?: PlaceholderMeasure };

export interface SubtextPlaceholder {
  key: string;
  metricId: string;
  name: string;
  measure?: PlaceholderMeasure;
}

const MEASURE_LABELS: Record<PlaceholderMeasure, string> = {
  delta: 'change',
  percentDelta: '% change',
};

const PLACEHOLDER = /\{([^{}]*)\}/g;

// "Total Drivers" → "totalDrivers"
export const getPlaceholderKey = (name: string): string =>
  _.camelCase(name).replace(/[^a-zA-Z0-9]/g, '');

// A placeholder per metric, in the block's order. Where two metrics give the
// same key the first keeps it.
export const getAvailablePlaceholders = ({
  metricIds,
  metricOptionsLookup,
}: {
  metricIds: string[];
  metricOptionsLookup: Record<string, Metrics.Metric | undefined>;
}): SubtextPlaceholder[] =>
  _.uniqBy(
    metricIds.flatMap((metricId): SubtextPlaceholder[] => {
      const metric = metricOptionsLookup[metricId];
      const key = metric ? getPlaceholderKey(metric.name) : '';
      return key ? [{ key, metricId, name: metric ? metric.name : '' }] : [];
    }),
    'key',
  );

// Each placeholder followed by its metric's change and % change from the
// comparison period, e.g. {totalDrivers}, {totalDrivers.delta} and
// {totalDrivers.percentDelta}
export const withComparisonPlaceholders = (
  placeholders: SubtextPlaceholder[],
): SubtextPlaceholder[] =>
  placeholders.flatMap((p) => [
    p,
    ...(Object.keys(MEASURE_LABELS) as PlaceholderMeasure[]).map((measure) => ({
      key: `${p.key}.${measure}`,
      metricId: p.metricId,
      name: `${p.name} · ${MEASURE_LABELS[measure]}`,
      measure,
    })),
  ]);

// Where a filled value is kept: by metric, and by measure for a change
export const getValueKey = ({
  metricId,
  measure,
}: {
  metricId: string;
  measure?: PlaceholderMeasure;
}): string => (measure ? `${metricId}.${measure}` : metricId);

// A metric's placeholder key showing the given measure: "totalDrivers",
// "totalDrivers.delta" or "totalDrivers.percentDelta"
export const getMeasureKey = ({
  key,
  measure,
}: {
  key: string;
  measure?: PlaceholderMeasure;
}): string => (measure ? `${key}.${measure}` : key);

const isMeasure = (value: string): value is PlaceholderMeasure =>
  Object.prototype.hasOwnProperty.call(MEASURE_LABELS, value);

// A placeholder key as its metric's key and what it shows of it. Only a
// change's own suffixes are split off.
export const splitMeasureKey = (
  key: string,
): { key: string; measure?: PlaceholderMeasure } => {
  const dot = key.lastIndexOf('.');
  const suffix = dot === -1 ? '' : key.slice(dot + 1);
  return isMeasure(suffix)
    ? { key: key.slice(0, dot), measure: suffix }
    : { key };
};

// The text split into literal text and metric values. Placeholders that
// don't name one of the block's metrics stay as they were typed, so a typo
// shows rather than vanishing.
export const parseSubtext = ({
  text,
  placeholders,
}: {
  text: string;
  placeholders: SubtextPlaceholder[];
}): SubtextPart[] => {
  const placeholderByKey = _.keyBy(placeholders, 'key');
  const parts: SubtextPart[] = [];
  const pushText = (value: string) => {
    if (!value) {
      return;
    }
    const last = parts[parts.length - 1];
    if (last && last.kind === 'text') {
      parts[parts.length - 1] = { kind: 'text', text: last.text + value };
    } else {
      parts.push({ kind: 'text', text: value });
    }
  };

  let lastIndex = 0;
  Array.from(text.matchAll(PLACEHOLDER)).forEach((match) => {
    const index = match.index ?? 0;
    pushText(text.slice(lastIndex, index));
    const placeholder = placeholderByKey[match[1].trim()];
    if (placeholder) {
      const { metricId, measure } = placeholder;
      parts.push(
        measure
          ? { kind: 'metric', metricId, measure }
          : { kind: 'metric', metricId },
      );
    } else {
      pushText(match[0]);
    }
    lastIndex = index + match[0].length;
  });
  pushText(text.slice(lastIndex));
  return parts;
};

// The metrics a text's placeholders name, once each, with what it shows of
// each: whether it needs the metric's value, its change, or both
export const getReferencedMeasures = ({
  text,
  placeholders,
}: {
  text: string;
  placeholders: SubtextPlaceholder[];
}): { metricId: string; measures: PlaceholderMeasure[] }[] => {
  const byMetric = new Map<string, PlaceholderMeasure[]>();
  parseSubtext({ text, placeholders }).forEach((part) => {
    if (part.kind !== 'metric') {
      return;
    }
    const measures = byMetric.get(part.metricId) ?? [];
    if (part.measure && !measures.includes(part.measure)) {
      measures.push(part.measure);
    }
    byMetric.set(part.metricId, measures);
  });
  return Array.from(byMetric, ([metricId, measures]) => ({
    metricId,
    measures,
  }));
};

// The metrics a text's placeholders name, once each
export const getReferencedMetricIds = ({
  text,
  placeholders,
}: {
  text: string;
  placeholders: SubtextPlaceholder[];
}): string[] =>
  getReferencedMeasures({ text, placeholders }).map(({ metricId }) => metricId);

// A filled value kept from being read as Markdown: its inline formatting
// characters, e.g. the '*' or '_' of a value
const MARKDOWN_INLINE = /[\\`*_[\]<>|~]/g;
export const escapeMarkdown = (value: string): string =>
  value.replace(MARKDOWN_INLINE, (c) => `\\${c}`);

// Plain text with its placeholders filled in, from values by getValueKey.
// Values not in yet show as '…'. Each can be escaped, e.g. for Markdown.
export const fillPlaceholders = ({
  text,
  placeholders,
  values,
  escape = _.identity,
}: {
  text: string;
  placeholders: SubtextPlaceholder[];
  values: Record<string, string>;
  escape?: (value: string) => string;
}): string =>
  parseSubtext({ text, placeholders })
    .map((part) =>
      part.kind === 'text'
        ? part.text
        : escape(values[getValueKey(part)] ?? '…'),
    )
    .join('');

// Rich text (HTML) with the placeholders in its text filled in. Values go in
// as text, so they're escaped, and attributes (e.g. links) are left alone. A
// placeholder split across styles (half of it bold) isn't recognised.
export const fillHtmlPlaceholders = ({
  html,
  placeholders,
  values,
}: {
  html: string;
  placeholders: SubtextPlaceholder[];
  values: Record<string, string>;
}): string => {
  if (!html.includes('{') || placeholders.length === 0) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Node[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }
  textNodes.forEach((node) => {
    const text = node.textContent ?? '';
    if (text.includes('{')) {
      node.textContent = fillPlaceholders({ text, placeholders, values });
    }
  });
  return doc.body.innerHTML;
};

const MAX_SUGGESTIONS = 8;

// The placeholder being typed at the caret: from an unclosed '{' followed by
// letters, digits and the '.' of a change's suffix only. Undefined when the
// caret isn't in one.
export const getPlaceholderQuery = ({
  text,
  caret,
}: {
  text: string;
  caret: number;
}): { start: number; query: string } | undefined => {
  const start = text.lastIndexOf('{', caret - 1);
  if (start === -1) {
    return undefined;
  }
  const query = text.slice(start + 1, caret);
  return /^[a-zA-Z0-9.]*$/.test(query) ? { start, query } : undefined;
};

// The placeholders a partly typed one could be: by key or metric name, those
// whose key starts with it first
export const matchPlaceholders = ({
  placeholders,
  query,
}: {
  placeholders: SubtextPlaceholder[];
  query: string;
}): SubtextPlaceholder[] => {
  const q = query.toLowerCase();
  const matches = placeholders.filter(
    (p) => p.key.toLowerCase().includes(q) || p.name.toLowerCase().includes(q),
  );
  return _.sortBy(matches, (p) =>
    p.key.toLowerCase().startsWith(q) ? 0 : 1,
  ).slice(0, MAX_SUGGESTIONS);
};

// The text with the partly typed placeholder (start to caret) replaced by
// the chosen one, and the caret after it
export const insertPlaceholder = ({
  text,
  start,
  caret,
  key,
}: {
  text: string;
  start: number;
  caret: number;
  key: string;
}): { text: string; caret: number } => {
  const placeholder = `{${key}}`;
  // Don't double a closing brace that's already there
  const rest = text.slice(caret);
  const after = rest.startsWith('}') ? rest.slice(1) : rest;
  return {
    text: text.slice(0, start) + placeholder + after,
    caret: start + placeholder.length,
  };
};
